import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FILTERS, PROJECTS } from "../data/site";
import { ProjectDrawer } from "./ProjectDrawer";

export const PortfolioGrid = () => {
  const [filter, setFilter] = useState("All");
  const [openIndex, setOpenIndex] = useState(null);

  const visible = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <section id="work" data-testid="portfolio-section" className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-32 scroll-mt-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
        <div>
          <p className="font-display text-xs uppercase tracking-[0.3em] font-semibold text-[#0047FF] mb-4">Selected Work</p>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-none">
            The proof<span className="text-[#0047FF]">.</span>
          </h2>
        </div>
        <p className="text-black/50 max-w-xs text-sm md:text-base">Tap any project to swipe through, share it, or message me about it.</p>
      </div>

      <div data-testid="portfolio-filters" className="flex gap-2 overflow-x-auto pb-4 mb-8 md:mb-12 -mx-5 px-5 md:mx-0 md:px-0 md:flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`portfolio-filter-${f.toLowerCase()}`}
            className={`shrink-0 font-display text-sm font-bold px-5 py-2.5 rounded-full border transition-colors ${
              filter === f
                ? "bg-[#0047FF] text-white border-[#0047FF]"
                : "bg-white text-black/60 border-black/10 hover:border-[#0047FF] hover:text-[#0047FF]"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <motion.div layout className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-8">
        <AnimatePresence mode="popLayout">
          {visible.map((p) => (
            <motion.button
              layout
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => setOpenIndex(PROJECTS.indexOf(p))}
              data-testid={`project-card-${p.id}`}
              className={`group text-left col-span-1 ${p.span} focus:outline-none focus:ring-2 focus:ring-[#0047FF] focus:ring-offset-2 rounded-lg`}
            >
              <div className={`overflow-hidden rounded-lg border border-black/5 bg-white ${p.ratio}`}>
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between mt-4">
                <div>
                  <h3 className="font-display font-bold text-lg md:text-xl tracking-tight">{p.title}</h3>
                  <p className="text-xs font-semibold uppercase tracking-widest text-black/40 mt-1">
                    {p.category} · {p.year}
                  </p>
                </div>
                <span className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center text-black/40 group-hover:bg-[#0047FF] group-hover:text-white group-hover:border-[#0047FF] transition-colors">
                  <ArrowUpRight size={18} />
                </span>
              </div>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>

      <ProjectDrawer openIndex={openIndex} setOpenIndex={setOpenIndex} />
    </section>
  );
};
